import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { api, ApiError } from '@/lib/api';
import { lockAppScroll, unlockAppScroll } from '@/lib/scrollLock';

export const CONCIERGE_STARTERS = [
  'How do I let a guest in?',
  'How do I get a gate transponder?',
  'Trash day?',
  'Enclave hours today',
  'Who do I call for a repair?',
  'Emergency numbers',
] as const;

interface Citation {
  title: string;
  source_label: string;
  source_date: string;
  page: number | null;
}

interface Turn {
  question: string;
  answer: string;
  citations: Citation[];
  error?: boolean;
}

interface ConciergeSheetProps {
  open: boolean;
  onClose: () => void;
}

export function ConciergeSheet({ open, onClose }: ConciergeSheetProps) {
  const [mounted, setMounted] = useState(false);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState('');
  const [pending, setPending] = useState(false);
  const askGen = useRef(0);

  function clearAnswer() {
    askGen.current += 1;
    setTurns([]);
    setPending(false);
  }

  useEffect(() => {
    if (!open) {
      askGen.current += 1;
      setTurns((prev) => (prev.length === 0 ? prev : []));
      setDraft((prev) => (prev === '' ? prev : ''));
      setPending((prev) => (prev ? false : prev));
      const t = window.setTimeout(() => setMounted(false), 200);
      return () => window.clearTimeout(t);
    }
    setMounted(true);
    lockAppScroll();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      unlockAppScroll();
    };
  }, [open, onClose]);

  async function ask(question: string) {
    const trimmed = question.trim();
    if (!trimmed || pending) return;
    const gen = ++askGen.current;
    setPending(true);
    setDraft('');
    setTurns([]);
    try {
      const res = await api.post<{ answer: string; citations: Citation[] }>('/concierge', { question: trimmed });
      if (gen !== askGen.current) return;
      setTurns([{ question: trimmed, answer: res.answer, citations: res.citations ?? [] }]);
    } catch (err) {
      if (gen !== askGen.current) return;
      const message = err instanceof ApiError ? err.message : 'Could not reach the concierge.';
      setTurns([{ question: trimmed, answer: message, citations: [], error: true }]);
    } finally {
      if (gen === askGen.current) setPending(false);
    }
  }

  if (!mounted && !open) return null;

  const showingAnswer = turns.length > 0;

  return createPortal(
    <div
      className={`action-menu ${open ? 'action-menu--open' : 'action-menu--closing'}`}
      role="dialog"
      aria-label="Concierge"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="action-menu__sheet concierge-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="action-menu__grabber" aria-hidden />
        <div className="action-menu__header">
          <div className="action-menu__title">Concierge</div>
        </div>

        <div className="concierge-sheet__body">
          {showingAnswer && (
            <button type="button" className="action-menu__close concierge-answer__close" onClick={clearAnswer}>
              Close
            </button>
          )}

          {showingAnswer && (
            <div className="concierge-sheet__thread">
              {turns.map((turn, index) => (
                <div key={`${turn.question}-${index}`}>
                  <div className="concierge-sheet__question">{turn.question}</div>
                  <div className="concierge-sheet__answer">{turn.answer}</div>
                  {turn.citations.length > 0 && (
                    <div className="concierge-sheet__cites">
                      {turn.citations.map((citation, citationIndex) => (
                        <span key={`${citation.source_label}-${citationIndex}`} className="concierge-cite">
                          {citation.source_label}
                          {citation.page != null ? `, p.${citation.page}` : ''}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {!showingAnswer && (
            <div className="concierge-starters">
              {CONCIERGE_STARTERS.map((starter) => (
                <button key={starter} type="button" className="concierge-chip" onClick={() => ask(starter)} disabled={pending}>
                  {starter}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="concierge-sheet__footer">
          <form
            className="concierge-ask"
            onSubmit={(e) => {
              e.preventDefault();
              void ask(draft);
            }}
          >
            <input
              aria-label="Question"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              disabled={pending}
            />
            <button type="submit" disabled={pending || draft.trim().length === 0}>
              {pending ? '…' : 'Ask'}
            </button>
          </form>

          <button type="button" className="action-menu__close" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
